import type {
  ComoPide,
  RedondeoAplicado,
} from '@/features/pricing-rules/types/pricing-rules.types';

export interface SmartPricingFactor {
  name: string;
  adjustment: number;
  weight: number;
  signal: string;
  dataAvailable: boolean;
}

export interface CompetitorInfo {
  name: string;
  price: number;
  source: string;
  date: string;
}

export interface ProductAnalysis {
  productId: string;
  name: string;
  sku: string;
  imageUrl: string | null;
  currentPrice: number;
  costPrice: number | null;
  recommendedPrice: number;
  changePercent: number;
  currentMargin: number | null;
  recommendedMargin: number | null;
  confidence: number;
  explanation: string;
  factors: SmartPricingFactor[];
  competitors: CompetitorInfo[];
  stock: number | null;
  monthlySalesVelocity: number;
}

export interface AnalysisResponse {
  products: ProductAnalysis[];
}

export type PriceStrategyCode =
  | 'MARGIN_TARGET'
  | 'MATCH_LOWEST'
  | 'UNDERCUT_PERCENT'
  | 'CUSTOM_FORMULA'
  | 'SMART_PRICING';

export interface RuleTraceEntry {
  ruleId: string;
  ruleName: string;
  priceBefore: number;
  priceAfter: number;
  warnings: string[];
}

export interface SmartPricingSnapshot {
  confidenceScore?: number;
  factors?: Record<string, any>;
  rulesTrace?: RuleTraceEntry[];
  matchedRuleIds?: string[];
  appliedDiscountIds?: string[];
  rawPriceBeforeRules?: number;
  // Spec 225 (simulate-strategies): quién manda, el redondeo y de dónde sale lo que pide.
  limitantes?: string[];
  redondeo?: RedondeoAplicado | null;
  comoPide?: ComoPide | null;
  [key: string]: any;
}

export interface StrategySimulationResult {
  price: number | null;
  priceDiff: number | null; // % vs currentPrice
  margin: number | null;
  confidence: number;
  error: string | null;
  snapshot: SmartPricingSnapshot | null;
}

export interface ProductStrategySimulation {
  productId: string;
  currentPrice: number;
  basePrice: number | null;
  costPrice: number | null;
  currentStrategy: PriceStrategyCode | null;
  strategies: Record<PriceStrategyCode, StrategySimulationResult>;
}

export interface StrategySimulationsResponse {
  simulations: ProductStrategySimulation[];
}

export interface StrategyConfig {
  targetMargin?: number; // % para MARGIN_TARGET
  undercutPercent?: number; // % para UNDERCUT_PERCENT
  minMarginPercent?: number; // % mínimo para SMART_PRICING
  bypassMarginGuardrail?: boolean; // ignorar el piso de margen mínimo (requiere confirmación explícita)
}

export const DEFAULT_STRATEGY_CONFIG: Required<StrategyConfig> = {
  targetMargin: 25,
  undercutPercent: 5,
  minMarginPercent: 10,
  bypassMarginGuardrail: false,
};

export interface CompetitorLink {
  storeName: string;
  storeId: string | null;
  price: number;
}

export interface LinkedProduct {
  productId: string;
  productName: string;
  productSku: string;
  productCategory: string | null;
  productImageUrl: string | null;
  currentPrice: number;
  costPrice: number | null;
  currentMargin: number | null;
  stock: number | null;
  strategy: PriceStrategyCode | null;
  strategyConfig: Record<string, any> | null;
  targetMargin: number | null;
  lastCalculatedPrice: number | null;
  lastCalculatedAt: string | null;
  originalPrice: number | null;
  diagnosisStrategyLabel: string | null;
  competitorCount: number;
  competitorLinks: CompetitorLink[];
  appliedAt: string | null;
  priceWasUpdated: boolean;
  appliedByUserId: string | null;
  appliedByUserName: string | null;
  rulesTrace?: RuleTraceEntry[] | null;
  wasReset?: boolean;
  lastResetAt?: string | null;
  lastResetFromPrice?: number | null;
  lastAppliedStrategy?: PriceStrategyCode | null;
}

export interface ProductHistoryEntry {
  id: string;
  action: string;
  createdAt: string;
  previousValue: any;
  newValue: any;
  triggerPrice: number;
  strategy: PriceStrategyCode | null;
  config: Record<string, any> | null;
  simulatedPrice: number | null;
  priceUpdated: boolean;
  userId: string | null;
  userName: string;
  /** Snapshot de simulación guardado al aplicar (factores SMART_PRICING, competidores, etc.) */
  snapshot?: any;
  /** Traza de reglas aplicadas cuando la entrada viene con reglas activas */
  rulesTrace?: RuleTraceEntry[] | null;
  /** Ids de descuentos aplicados al simular esta entrada */
  appliedDiscountIds?: string[] | null;
  /** Si true, la estrategia se aplicó usando el precio base original como punto de partida */
  basePriceUsed?: boolean;
  details?: any;
  /** Precio base original desde el que se calculó la estrategia */
  basePriceValue?: number | null;
}

export interface ProductHistoryResponse {
  product: {
    id: string;
    name: string;
    sku: string;
    currentPrice: number;
    strategy: PriceStrategyCode;
  };
  history: ProductHistoryEntry[];
}

export interface OutcomeData {
  window: number;
  windowStart: string;
  windowEnd: string;
  hasData: boolean;
  totalUnits: number;
  totalRevenue: number;
  avgSellingPrice: number | null;
  velocityChangePct: number | null;
  baseline: {
    stockAtApply: number | null;
    velocityAtApply: number | null;
    marginAtApply: number | null;
  } | null;
  dataPoints: number;
}

export interface StrategyEffectivenessEntry {
  strategy: string;
  appliedCount: number;
  avgPriceChangePct: number | null;
  priceWentUp: number;
  priceWentDown: number;
  priceUnchanged: number;
  hasBaselineData: boolean;
}

export interface StrategyEffectivenessResponse {
  window: number;
  since: string;
  strategies: StrategyEffectivenessEntry[];
}

export interface LinkedProductsResponse {
  products: LinkedProduct[];
  total: number;
  page: number;
  totalPages: number;
  summary: {
    totalLinked: number;
    priceUpdatedCount: number;
    distinctStrategies: number;
    last24hCount: number;
  };
}

// ── Market Overview (Fase 2) ──────────────────────────────────────────────

export type CpiPosition = 'VERY_BELOW' | 'BELOW' | 'ALIGNED' | 'ABOVE' | 'VERY_ABOVE';

export interface MarketStoreEntry {
  storeId: string;
  storeName: string;
  storeLogoUrl: string | null;
  price: number;
  externalUrl: string | null;
  lastScrapedAt: string | null;
  isCheapest: boolean;
  isMostExpensive: boolean;
}

export interface MarketProductEntry {
  productId: string;
  productName: string;
  productSku: string;
  productCategory: string | null;
  currentPrice: number;
  costPrice: number | null;
  currentMargin: number | null;
  marketMin: number;
  marketMax: number;
  marketAvg: number;
  marketMedian: number;
  competitorCount: number;
  gapVsMin: number | null;
  cpi: number;
  cpiPosition: CpiPosition;
  cpiLabel: string;
  cpiColor: string;
  priceBand: { min: number; max: number; optimal: number };
  recommendedPrice: number;
  recommendedRationale: string;
  byStore: MarketStoreEntry[];
}

export interface MarketOverviewSummary {
  totalWithCompetitors: number;
  totalProducts: number;
  avgCpi: number;
  byPosition: Record<CpiPosition, number>;
}

export interface MarketOverviewResponse {
  products: MarketProductEntry[];
  total: number;
  page: number;
  limit: number;
  summary: MarketOverviewSummary;
}

export const CPI_CONFIG: Record<CpiPosition, { label: string; short: string }> = {
  VERY_BELOW: {
    label: 'Precio muy bajo',
    short: 'Muy bajo',
  },
  BELOW: {
    label: 'Por debajo del mercado',
    short: 'Bajo',
  },
  ALIGNED: {
    label: 'Alineado al mercado',
    short: 'Alineado',
  },
  ABOVE: {
    label: 'Por encima del mercado',
    short: 'Alto',
  },
  VERY_ABOVE: {
    label: 'Precio muy alto',
    short: 'Muy alto',
  },
};

/**
 * Plan 3, T8: los mismos nombres que el asistente (`METODOS` en
 * `nueva-estrategia/paso-2-metodo.tsx`); `strategy-labels.test.ts` los compara.
 */
export const STRATEGY_LABELS: Record<PriceStrategyCode, string> = {
  MARGIN_TARGET: 'Apuntar a un margen',
  MATCH_LOWEST: 'Igualar al más barato',
  UNDERCUT_PERCENT: 'Quedar por debajo del más barato',
  CUSTOM_FORMULA: 'Fórmula propia',
  SMART_PRICING: 'Precio inteligente',
};

export const STRATEGY_DESCRIPTIONS: Record<PriceStrategyCode, string> = {
  MARGIN_TARGET: 'Precio para ganar el margen elegido sobre el precio de venta',
  MATCH_LOWEST: 'Iguala al competidor más barato detectado',
  UNDERCUT_PERCENT: 'Se posiciona un porcentaje por debajo del más barato',
  CUSTOM_FORMULA: 'Usa la fórmula personalizada asignada al producto',
  SMART_PRICING: 'Ajuste multi-factor: mercado, demanda, inventario, elasticidad',
};

/** Información detallada de cada estrategia para mostrar al cliente */
export interface StrategyDetail {
  /** Explicación en lenguaje natural de qué hace la estrategia */
  description: string;
  /** Fórmula o lógica expresada de forma simple */
  formula: string;
  /** Ejemplo concreto con números */
  example: string;
  /** Guía de cuándo conviene usarla */
  whenToUse: string;
  /** Qué datos necesita para funcionar */
  requires: string;
}

export interface StrategyGroupProduct {
  id: string;
  name: string;
  sku: string;
  currentPrice: number;
  costPrice: number | null;
}

export interface StrategyGroup {
  key: string;
  label: string;
  count: number;
  productIds: string[];
  products: StrategyGroupProduct[];
  avgCpi: number;
  avgMargin: number | null;
  cpiDist: Record<CpiPosition, number>;
}

export interface StrategyGroupsResponse {
  groups: StrategyGroup[];
  total: number;
  groupBy: string;
}

// ── Señales Internas ─────────────────────────────────────────────────────────

export type SignalType =
  | 'STOCKOUT_RISK'
  | 'BELOW_MIN_MARGIN'
  | 'DEAD_STOCK'
  | 'LOW_STOCK'
  | 'SLOW_MOVER'
  | 'BELOW_TARGET_MARGIN'
  | 'DECLINING_DEMAND'
  | 'HOLDING_COST_PRESSURE'
  | 'NO_SALES_TRACTION'
  | 'EXCLUSIVE_PRODUCT'
  | 'GROWING_DEMAND'
  | 'PRICE_GAP'
  | 'MARKET_HOLE'
  | 'GUARDRAIL_TRAP';

export type SignalUrgency = 'CRITICAL' | 'WARNING' | 'OPPORTUNITY';

export interface Signal {
  type: SignalType;
  urgency: SignalUrgency;
  message: string;
  suggestedPrice: number | null;
  suggestedPriceReason: string | null;
  dataUsed: Record<string, number | string | null>;
  missingData: string[];
  estimatedMonthlyImpactBs: number | null;
}

export interface ProductSignal {
  productId: string;
  productName: string;
  sku: string;
  category: string | null;
  currentPrice: number;
  signals: Signal[];
  topSignal: Signal;
}

export interface SignalsSummary {
  total: number;
  critical: number;
  warning: number;
  opportunity: number;
  productsAffected: number;
}

export interface SignalsResponse {
  summary: SignalsSummary;
  signals: ProductSignal[];
}

// ── Diagnóstico de Catálogo ───────────────────────────────────────────────────

export type DiagnosisGroupType = 'SIGNAL' | 'MARKET' | 'CLEAN';

export interface DiagnosisStrategy {
  code: string;
  label: string;
  description: string;
  formula: string;
  isRecommended: boolean;
  direction: 'UP' | 'DOWN' | 'NEUTRAL';
}

export interface DiagnosisProduct {
  productId: string;
  productName: string;
  sku: string;
  category: string | null;
  currentPrice: number;
  costPrice: number | null;
  suggestedPrice: number | null;
  suggestedPriceReason: string | null;
  priceDiffPct: number | null;
  signalMessage: string | null;
  cpi: number | null;
  competitorCount: number;
  dataUsed: Record<string, number | string | null>;
  missingData: string[];
  // Contexto operacional
  stock: number | null;
  lastSaleDate: string | null;
  monthlySalesVelocity: number | null;
  sales30d: number;
  currentMargin: number | null;
  daysCoverage: number | null;
  strategyPrices: Record<string, number | null>;
}

export interface DiagnosisGroupKpis {
  avgCurrentPrice: number | null;
  avgSuggestedPrice: number | null;
  avgPriceDiffPct: number | null;
  avgCpi: number | null;
  productsWithSuggestedPrice: number;
}

export interface DiagnosisGroup {
  id: string;
  type: DiagnosisGroupType;
  signalType: string | null;
  urgency: SignalUrgency | 'OK';
  label: string;
  description: string;
  formula: string;
  recommendedAction: string;
  strategies: DiagnosisStrategy[];
  count: number;
  kpis: DiagnosisGroupKpis;
  products: DiagnosisProduct[];
}

export interface DiagnosisSummary {
  critical: number;
  warning: number;
  opportunity: number;
  withMarketData: number;
  noActionNeeded: number;
  total: number;
}

export interface DiagnosisResponse {
  summary: DiagnosisSummary;
  groups: DiagnosisGroup[];
}

export const SIGNAL_CONFIG: Record<SignalType, { label: string; short: string }> = {
  STOCKOUT_RISK: { label: 'Riesgo de quiebre', short: 'Quiebre' },
  BELOW_MIN_MARGIN: { label: 'Bajo margen mínimo', short: 'Margen mínimo' },
  DEAD_STOCK: { label: 'Inventario inmovilizado', short: 'Dead stock' },
  LOW_STOCK: { label: 'Stock bajo mínimo', short: 'Stock bajo' },
  SLOW_MOVER: { label: 'Rotación lenta', short: 'Rotación lenta' },
  BELOW_TARGET_MARGIN: { label: 'Bajo margen objetivo', short: 'Margen objetivo' },
  DECLINING_DEMAND: { label: 'Demanda cayendo', short: 'Demanda baja' },
  HOLDING_COST_PRESSURE: { label: 'Costo almacenaje alto', short: 'Almacenaje' },
  NO_SALES_TRACTION: { label: 'Sin tracción de ventas', short: 'Sin ventas' },
  EXCLUSIVE_PRODUCT: { label: 'Producto exclusivo', short: 'Exclusivo' },
  GROWING_DEMAND: { label: 'Demanda creciente', short: 'Crecimiento' },
  PRICE_GAP: { label: 'Precio sobre el mercado', short: 'Sobre mercado' },
  MARKET_HOLE: { label: 'Hueco de mercado', short: 'Hueco mercado' },
  GUARDRAIL_TRAP: { label: 'Estrategia trabada por una regla', short: 'Trabada' },
};

// ── Salud del Catálogo (Catalog Health) ─────────────────────────────────────

export type AbcClass = 'A' | 'B' | 'C';
export type XyzClass = 'X' | 'Y' | 'Z';
export type DemandTrend = 'GROWING' | 'STABLE' | 'DECLINING' | 'NO_MOVEMENT';
export type InventoryQuadrant =
  | 'ESTRELLA'
  | 'SOBRESTOCK'
  | 'RIESGO_QUIEBRE'
  | 'DEAD_STOCK'
  | 'SIN_DATOS';

export interface CatalogHealthProductSummary {
  id: string;
  name: string;
  sku: string;
  category: string | null;
  currentPrice: number;
  costPrice: number | null;
  stock: number;
  minStock: number | null;
  leadTimeDays: number | null;
  monthlySalesVelocity: number | null;
  abcClass: AbcClass;
  xyzClass: XyzClass | null;
  gmroi: number | null;
  daysCoverage: number | null;
  sellThrough: number | null;
  trend: DemandTrend;
  momentum: number | null;
  sales30d: number;
  sales30to60d: number;
  lastSaleDate: string | null;
  cpi: number | null;
  hasCompetitorData: boolean;
  revenueMensual: number;
  capitalInmovilizado: number;
  inventoryQuadrant: InventoryQuadrant;
}

export interface CatalogHealthSummary {
  totalProducts: number;
  gmroiPromedio: number;
  inventoryTurnover: number;
  capitalInmovilizado: number;
  sellThroughPromedio: number;
  dataGaps: { sinCosto: number; sinStock: number; sinVentas90d: number; sinVinculo: number };
  abcCounts: { A: number; B: number; C: number };
  xyzCoverage: number;
}

export interface CatalogHealthResponse {
  summary: CatalogHealthSummary;
  abcxyz: {
    matrix: Partial<Record<string, number>>;
    products: Partial<Record<string, CatalogHealthProductSummary[]>>;
  };
  inventoryQuadrant: Record<InventoryQuadrant, CatalogHealthProductSummary[]>;
  demandTrend: Record<DemandTrend, CatalogHealthProductSummary[]>;
  products: CatalogHealthProductSummary[];
}

export const STRATEGY_DETAILS: Record<PriceStrategyCode, StrategyDetail> = {
  MARGIN_TARGET: {
    description:
      'Calcula el precio para que de cada venta te quede el margen elegido, medido sobre el precio de venta (como en el resto de Prixia). Es la estrategia más simple y predecible: cada venta deja ese margen.',
    formula: 'Precio = Costo ÷ (1 − Margen %)',
    example:
      'Si el costo es Bs 50 y querés un margen del 30 %, el precio recomendado es Bs 71,43 (50 ÷ 0,70): de esos 71,43, 21,43 son margen, el 30 %.',
    whenToUse:
      'Cuando querés garantizar rentabilidad por unidad vendida sin importar el comportamiento del mercado. Ideal para productos con costo estable y márgenes conocidos.',
    requires: 'Costo del producto cargado en el sistema',
  },
  MATCH_LOWEST: {
    description:
      'Iguala tu precio al del competidor más barato detectado en el mercado. Mantiene competitividad total sin regalar más margen del necesario.',
    formula: 'Precio = mínimo(precios de todos los competidores)',
    example:
      'Si tenés 3 competidores vendiendo a Bs 120, Bs 105 y Bs 98, tu precio recomendado es Bs 98 (el más barato).',
    whenToUse:
      'Cuando tu objetivo es no ser más caro que nadie pero tampoco sacrificar margen extra. Útil en productos donde el precio es el único diferencial.',
    requires: 'Al menos un precio de la competencia (leído de su tienda o cargado a mano)',
  },
  UNDERCUT_PERCENT: {
    description:
      'Se posiciona un porcentaje específico por debajo del competidor más barato. Es la estrategia más agresiva: siempre vas a ser más barato que cualquier otro vendedor.',
    formula: 'Precio = mínimo(competencia) × (1 − tu % / 100)',
    example:
      'Si el competidor más barato está en Bs 100 y elegís quedar 5 % por debajo, tu precio queda en Bs 95.',
    whenToUse:
      'Para estrategias de penetración de mercado, liquidaciones, o cuando necesitás captar volumen rápido ganando share contra la competencia.',
    requires:
      'Al menos un precio de la competencia y el % por debajo del más barato que querés quedar',
  },
  SMART_PRICING: {
    description:
      'Calcula el precio analizando 5 señales al mismo tiempo: posición vs mercado, demanda (velocidad de ventas), nivel de stock, elasticidad histórica del precio y un piso de margen mínimo. El sistema ajusta automáticamente hasta ±20% sobre el precio base.',
    formula: 'Precio base × (1 + ajuste ponderado según 5 factores)',
    example:
      'Producto con stock bajo (3 días restantes) + demanda alta → sube 5%. Producto con stock alto (120 días) + ventas frías → baja 5%. Todo con garantía de margen mínimo.',
    whenToUse:
      'Para optimización automática continua cuando tenés datos históricos confiables. Ideal para catálogos grandes donde no podés analizar producto por producto manualmente.',
    requires:
      'Costo + competencia + historial de ventas (mínimo 30 días) + stock + margen mínimo configurado',
  },
  CUSTOM_FORMULA: {
    description:
      'Usa la fórmula matemática que vos mismo definiste en el módulo "Modelos de Precio". Podés combinar variables del producto (costo, stock), del mercado (precio mínimo/promedio de competencia), y variables externas (tipo de cambio, inflación, etc.).',
    formula: 'Definida por vos en el Modelo de Precio asignado al producto',
    example:
      'Podés crear fórmulas tipo: "Precio = Costo × 1.4 × (1 + Inflación) × (tramo por volumen)". Depende 100% de cómo la armés.',
    whenToUse:
      'Para reglas de pricing específicas de tu negocio que no encajan en las otras 4 estrategias. Útil cuando tenés lógica por tramos, factores externos o cálculos complejos propios.',
    requires: 'Un Modelo de Precio creado en "Modelos" y asignado al producto en Precios Dinámicos',
  },
};
